import React, { useState, useRef, useEffect, useContext } from "react";
import { Col, Container, Row, Dropdown, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ExcellentExport from "excellentexport";
import ReactToPrint from "react-to-print";
import PropTypes from "prop-types";
import ContentTable from "../table/ContentTable";
import { Table } from "../table/Table";
import ContentSolid from "../global/ContentSolid";
import { LoadingTable } from "../lazyLoading/LazyLoading";
import {
  GetLevelsForScore,
  GetScores,
  GetScoresReport,
  GetScoresReportPdf,
  GetScoresReportXlsx,
} from "../../api/cyclesForScores";
import * as Sentry from "@sentry/react";
import FeatureFlagWrapper from "../global/FeatureFlagWrapper";

// Import Custom Components
import ModalGradingCriteria from "../class/modals/ModalGradingCriteria";
import { isFeatureEnabled, validateUserRoleIds } from "../global/GlobalTools";
import ModalReleaseRatings from "./modals/ModalReleaseRatings";

// Import Context
import { MainContext } from "../../App";

// Record reporting options
const actionOptions = [
  { value: "list", label: "global:buttons.downloadPDF" },
  { value: "excel", label: "global:buttons.downloadExcel" },
];

const ScoresView = () => {
  const isFFReportScores = isFeatureEnabled("Reporte_Calificaciones_Detallado");
  // Ref always holds the latest FF value — used to guard stale async callbacks
  const isFFReportScoresRef = useRef(isFFReportScores);
  isFFReportScoresRef.current = isFFReportScores;
  const componentRef = useRef(); // Create the reference for printing
  const [t] = useTranslation(["global", "class"]);
  const [filters, setFilters] = useState({
    cycle: null,
    level: null,
    subjects: null,
    grade: null,
    group: null,
    programs: null,
    levelProgram: null,
    period: null,
    class: null,
  });

  // Set data
  const [schoolLevels, setSchoolLevels] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [grades, setGrades] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [levels, setLevels] = useState([]);
  const [groups, setGroups] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingDownload, setLoadingDownload] = useState(null);
  const [printExport, setPrintExport] = useState(true);
  const [periodSelected, setPeriodSelected] = useState(false);
  const [filterSubjectsValue, setFilterSubjectsValue] = useState("");
  const [exportData, setExportData] = useState([]);
  const [count, setCount] = useState(0);
  const [selectSchoolLevelsOptions, setSelectSchoolLevelsOptions] = useState(
    [],
  );
  const [showModalReleaseRatings, setShowModalReleaseRatings] = useState(false);
  const { permissions } = useContext(MainContext);

  // Options to export to excel
  const exportOptions = {
    openAsDownload: true,
    format: "xlsx", //'xlsx' or 'xls' or 'csv'
    filename: formatFilename(filters),
  };

  const [cyclesForScores, setCyclesForScores] = useState([]);
  const [studentForTable, setStudentForTable] = useState([]);
  // Render-phase guard: if isFFReportScores changed (e.g. after hot reload with
  // preserved state), discard stale table data before React commits the render.
  // This prevents "Objects are not valid as a React child" when the data format
  // from the previous FF state no longer matches the current columns.
  const [prevIsFFReportScores, setPrevIsFFReportScores] = useState(
    isFFReportScores,
  );
  if (prevIsFFReportScores !== isFFReportScores) {
    setPrevIsFFReportScores(isFFReportScores);
    setStudentForTable([]);
  }
  // Table columns
  const data = React.useMemo(() => studentForTable, [studentForTable]);

  const columnsForFFReportScores = () => {
    const baseColumns = [
      {
        Header: t("class:table.enrollment"),
        accessor: "student_unique_id",
        minWidth: 120,
        width: 120,
        maxWidth: 120,
        hideFilter: true,
        hideSort: true,
      },
      {
        Header: t("class:table.studentName"),
        accessor: "student_name",
        minWidth: 220,
        width: 220,
        maxWidth: 220,
        hideFilter: true,
        hideSort: true,
      },
      {
        Header: t("class:table.assessment"),
        accessor: "assessment",
        minWidth: 120,
        width: 120,
        maxWidth: 120,
        hideFilter: true,
        hideSort: true,
        Cell: () => {
          const periodText = t("class:table.period");
          const partialText = t("class:table.partial");
          if (periodSelected) {
            return (
              <>
                {periodSelected.partials.map((partial) => (
                  <p
                    key={partial.accessor}
                  >{`${partialText} ${partial.label}`}</p>
                ))}
                <p>{`${periodText} ${periodSelected.label}`}</p>
              </>
            );
          }
          return periods.map((period) => (
            <p key={period.accessor}>{`${periodText} ${period.label}`}</p>
          ));
        },
      },
      ...(filters.class
        ? [filters.class]
        : [...classes].sort((a, b) =>
            (a.name ?? a.title ?? "").localeCompare(b.name ?? b.title ?? ""),
          )
      ).map((classItem) => ({
        Header: classItem.name ?? classItem.title,
        accessor: `class_${classItem.id}`,
        minWidth: 90,
        width: 90,
        maxWidth: 90,
        hideFilter: true,
        hideSort: true,
        Cell: ({ value }) => {
          if (periodSelected) {
            const period = value?.find(
              (p) => String(p.id) === String(periodSelected.id),
            );
            const partials =
              period?.partials && period?.partials.length > 0
                ? period?.partials
                : periodSelected?.partials;
            return (
              <>
                {partials.map((partial) => (
                  <p key={partial?.id}>{partial?.score ?? "-"}</p>
                ))}
                <p>{period?.score ?? "-"}</p>
              </>
            );
          }
          return periods.map((period) => {
            const found = value?.find(
              (p) => String(p.id) === String(period.id),
            );
            return <p key={period.id}>{found?.score ?? "-"}</p>;
          });
        },
      })),
      {
        Header: t("class:table.average"),
        accessor: "average",
        minWidth: 90,
        width: 90,
        maxWidth: 90,
        hideFilter: true,
        hideSort: true,
        Cell: ({ value }) => {
          if (periodSelected) {
            const period = value?.find(
              (p) => String(p.id) === String(periodSelected.id),
            );
            const partials =
              period?.partials && period?.partials.length > 0
                ? period?.partials
                : periodSelected?.partials;
            return (
              <>
                {partials.map((partial) => (
                  <p key={partial?.id}>{partial?.score ?? "-"}</p>
                ))}
                <p>{period?.score ?? "-"}</p>
              </>
            );
          }
          return periods.map((period) => {
            const found = value?.find(
              (p) => String(p.id) === String(period.id),
            );
            return <p key={period.id}>{found?.score ?? "-"}</p>;
          });
        },
      },
    ];

    return baseColumns;
  };

  //Data for table
  const columns = React.useMemo(() => {
    // Returns the columns based on the active feature flags.
    // Each FF branch is fully responsible for its own column set.
    const getColumns = () => {
      if (isFFReportScores) {
        return columnsForFFReportScores();
      }

      // Original columns (no FF active)
      const defaultColumns = [
        {
          Header: t("class:table.studentName"),
          accessor: "student_name",
          minWidth: 250,
          width: 250,
          maxWidth: 250,
          hideFilter: true,
          hideSort: true,
        },
        {
          Header: t("class:main.className"),
          accessor: "class_name",
          minWidth: 250,
          width: 250,
          maxWidth: 250,
          hideFilter: true,
          hideSort: true,
        },
      ];

      let periodsColumns = [];
      if (periods.length) {
        periodsColumns = periods.map((period) => ({
          Header: t("class:table.period") + " " + period.label,
          accessor: period.accessor,
          minWidth: 50,
          width: 75,
          maxWidth: 100,
          hideFilter: true,
          hideSort: true,
        }));
      }

      if (periodSelected) {
        defaultColumns.push({
          Header: t("class:table.period") + " " + periodSelected.label,
          accessor: periodSelected.accessor,
          minWidth: 50,
          width: 75,
          maxWidth: 100,
          hideFilter: true,
          hideSort: true,
        });
      } else if (periodsColumns.length) {
        defaultColumns.push(...periodsColumns, {
          Header: t("class:table.average"),
          accessor: "average",
          minWidth: 100,
          width: 125,
          maxWidth: 150,
          hideFilter: true,
          hideSort: true,
        });
      }

      return defaultColumns;
    };

    return getColumns();
  }, [loading, filters, periods, periodSelected, classes]);

  /**
   * Obtains all informations
   */
  const getCyclesFullInformation = () => {
    GetLevelsForScore()
      .then(async (result) => {
        if (result?.description) {
          Sentry.captureException(Error(JSON.stringify(result)));
        } else {
          const cycles = result.data?.data || [];
          // The information obtained is saved in the state
          setCyclesForScores(cycles);
          // Get the current cycle level
          const currentCycle =
            cycles.find((cycle) => cycle.current_cycle == 1) || cycles[0];
          // Get school levels for current cycle
          const schoolLevels = currentCycle?.school_levels || [];
          const firstSchoolLevel = schoolLevels?.[0] || {};
          setSchoolLevels(schoolLevels);
          // Format school levels for select
          formatLevels(schoolLevels);
          // Set default filters with current cycle information
          const newFilters = getDefaultFilters(currentCycle);
          setFilters(newFilters);
          // Set subjects for current cycle and school level
          setSubjects(firstSchoolLevel?.subjects || []);
          // Format periods
          await formatPeriods(firstSchoolLevel?.grading_periods || []);
          //Fill grade or level offers.
          const subjects = firstSchoolLevel?.subjects || [];
          const firstSubject = subjects?.[0] || {};
          if (firstSchoolLevel?.type == "superior") {
            setPrograms(firstSubject?.programs);
            if (isFFReportScores) {
              setLevels(firstSubject?.programs?.[0]?.program_levels);
              // setClasses(
              //   firstSubject?.programs?.[0]?.program_levels?.[0]?.classes || [],
              // );
              getAllClassesByLevel(newFilters);
            }
          } else if (firstSchoolLevel?.type == "basic") {
            setGrades(firstSubject?.grades);
            if (isFFReportScores) {
              setGroups(firstSubject?.grades?.[0]?.groups);
              // setClasses(firstSubject?.grades?.[0]?.groups?.[0]?.classes || []);
              getAllClassesByLevel(newFilters);
            }
          }
          GetScoresFullInfo(newFilters);
        }
      })
      .catch(() => {
        setLoading(false);
      });
  };

  /**
   * Get all classes by level
   * @param {object} filters - Filters object
   * @returns {array}
   */
  const getAllClassesByLevel = (filters) => {
    const newFilters = { ...filters };
    const level = newFilters.level;
    const isBasic = level.type === "basic";
    const filterByGradeProgramId = isBasic
      ? newFilters.grade?.id
      : newFilters.programs?.id;
    const filterByGroupLevelProgramId = isBasic
      ? newFilters.group?.id
      : newFilters.levelProgram?.id;
    const property1 = isBasic ? "grades" : "programs";
    const property2 = isBasic ? "groups" : "program_levels";
    const classes = [];
    const grades = [];
    const groups = [];
    const programs = [];
    const levels = [];
    const subjects = [];
    const seenClasses = new Set();
    const seenTier1 = new Set();
    const seenTier2 = new Set();
    const seenSubjects = new Set();
    // When a subject is selected, only collect classes for that subject.
    // Grades and groups are always collected from all subjects (same across subjects).
    const selectedSubjectId = newFilters.subjects?.id;
    for (const subject of level.subjects) {
      const collectClasses =
        !selectedSubjectId || subject.id === selectedSubjectId;
      // Verificar si la materia existe en el programa
      const gradeProgram = subject[property1].find(
        (item) => item.id === filterByGradeProgramId,
      );
      const groupLevelProgram = gradeProgram
        ? gradeProgram[property2].find(
            (item) => item.id === filterByGroupLevelProgramId,
          )
        : null;
      // Verificando si exite ya la clase en el array
      if (gradeProgram && groupLevelProgram && !seenSubjects.has(subject.id)) {
        // Agregando registro de entrada
        seenSubjects.add(subject.id);
        // Insertando materia al array
        subjects.push(subject);
      }
      // Iterando los grados/programas para obtenerlos todos
      for (const grade of subject[property1]) {
        if (!seenTier1.has(grade.id)) {
          seenTier1.add(grade.id);
          if (isBasic) {
            grades.push(grade);
          } else {
            programs.push(grade);
          }
        }
        // Iterando los grupos/niveles de programa para obtenerlos todos
        for (const group of grade[property2]) {
          if (
            (!filterByGradeProgramId || grade.id === filterByGradeProgramId) &&
            !seenTier2.has(group.id)
          ) {
            seenTier2.add(group.id);
            if (isBasic) {
              groups.push(group);
            } else {
              levels.push(group);
            }
          }
          if (
            collectClasses &&
            (!filterByGradeProgramId || grade.id === filterByGradeProgramId) &&
            group.id === filterByGroupLevelProgramId
          ) {
            for (const clas of group.classes) {
              if (!seenClasses.has(clas.id)) {
                seenClasses.add(clas.id);
                classes.push(clas);
              }
            }
          }
        }
      }
    }
    setSubjects([...subjects]);
    setClasses([...classes]);
    newFilters.class = null;
    setFilters({ ...newFilters });
    if (isBasic) {
      setGrades(grades);
      setGroups(groups);
    } else {
      setPrograms(programs);
      setLevels(levels);
    }
    return classes;
  };

  /**
   * Get default filters with current cycle information
   * @param {object} currentCycle
   * @return {object}
   */
  const getDefaultFilters = (currentCycle) => {
    const newFilters = { ...filters };
    newFilters.cycle = currentCycle;
    newFilters.level = currentCycle?.school_levels?.[0] || false;
    newFilters.subjects =
      currentCycle?.school_levels?.[0]?.subjects?.[0] || false;
    if (isFFReportScores) {
      newFilters.subjects = false; // TODO: Show all subjects
      const firstSchoolLevel = currentCycle?.school_levels?.[0];
      const firstSubject = firstSchoolLevel?.subjects?.[0];
      if (firstSchoolLevel?.type === "superior") {
        newFilters.programs = firstSubject?.programs?.[0];
        newFilters.levelProgram =
          firstSubject?.programs?.[0]?.program_levels?.[0];
      } else if (firstSchoolLevel?.type === "basic") {
        newFilters.grade = firstSubject?.grades?.[0];
        newFilters.group = firstSubject?.grades?.[0]?.groups?.[0];
      }
    }
    return newFilters;
  };

  /**
   * Formats school levels for select
   * @param {Array} [schoolLevels=[]]
   */
  const formatLevels = (schoolLevels = []) => {
    const formatLevels = schoolLevels.reduce((acc, level) => {
      if (level.type === "basic") {
        acc.push({
          label: level.name,
          value: level.organization_school_level_cycle_id,
        });
      }
      return acc;
    }, []);
    setSelectSchoolLevelsOptions(formatLevels);
  };

  /**
   * Formats periods for table columns
   * @param {Array} [periods=[]]
   */
  const formatPeriods = async (periods = []) => {
    for (const key in periods) {
      periods[key].label = Number.parseInt(key) + 1;
      periods[key].accessor = "period" + periods[key].id;
      periods[key].partials = periods[key].partials ?? [];
      periods[key].partials = periods[key].partials.map((partial, index) => ({
        label: index + 1,
        accessor: `partial_${partial}`,
      }));
    }
    setPeriods(periods);
  };

  /**
   * Get full information to Scores
   */
  const GetScoresFullInfo = (newFilters = filters) => {
    if (
      !newFilters.cycle ||
      !newFilters.level ||
      (!newFilters.subjects && !isFFReportScores)
    ) {
      setStudentForTable([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    let request = {
      school_cycle_id: newFilters.cycle.id,
      school_level_id: newFilters.level.school_level_id,
    };
    if (newFilters.subjects?.id) {
      request.subject_id = newFilters.subjects.id;
    }
    if (newFilters.programs?.id) {
      request.program_id = newFilters.programs.id;
    }
    if (newFilters.levelProgram?.id) {
      request.program_level_id = newFilters.levelProgram.id;
    }
    if (newFilters.grade?.id) {
      request.grade_level_id = newFilters.grade.id;
    }
    if (newFilters.group?.id) {
      request.grade_group_id = newFilters.group.id;
      request.grade_group_grade_level_id =
        newFilters.group.grade_group_grade_level_id;
    }
    if (newFilters.class?.id) {
      request.class_id = newFilters.class.id;
    }
    if (isFFReportScores) {
      /**
       * Payload to obtain the scores report
       * school_cycle_id: newFilters.cycle.id, // Required
       * grade_group_grade_level_id: newFilters.group.grade_group_grade_level_id, // Optional
       * program_id: newFilters.programs.id, // Optional
       * program_level_id: newFilters.levelProgram.id, // Optional
       * subject_id: newFilters.subjects.id, // Optional
       * class_id: newFilters.class.id, // Optional
       */
      delete request.school_level_id;
      delete request.grade_group_id;
      delete request.grade_level_id;
      getScoresReport(request);
      return;
    }
    GetScores(request)
      .then((result) => {
        if (result?.description) {
          Sentry.captureException(Error(JSON.stringify(result)));
        } else {
          let students = result.data.data;
          if (result.status == 200 && students && students.length > 0) {
            for (const student of students) {
              const scores = student?.scores;
              for (const i of scores) {
                let prop = "period" + i.period_id;
                student[prop] = i.score || "-";
              }
            }
            setStudentForTable(students);
          } else {
            setStudentForTable([]);
          }
        }
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
      });
  };

  /**
   * Get full information to Scores Report
   * @param {*} payload
   */
  const getScoresReport = (payload) => {
    GetScoresReport(payload)
      .then((result) => {
        // Guard: if FF was disabled while the request was in-flight, discard the
        // response to prevent writing FF=true format data into the table.
        if (!isFFReportScoresRef.current) return;
        if (result?.description) {
          Sentry.captureException(Error(JSON.stringify(result)));
        } else {
          const students = result.data;
          if (students?.length > 0) {
            const flatStudents = students.map((student) => {
              const flat = {
                student_id: student.student_id,
                student_name: student.student_name,
                student_unique_id: student.student_unique_id,
              };
              flat.average = student.average_grading_periods ?? [];
              for (const classItem of student.classes ?? []) {
                flat[`class_${classItem.id}`] = classItem.grading_periods ?? [];
              }
              return flat;
            });
            setStudentForTable(flatStudents);
          } else {
            setStudentForTable([]);
          }
        }
      })
      .finally(() => setLoading(false));
  };

  /**
   * Downloads the scores report as PDF or XLSX via API
   * @param {String} documentType PDF/XLSX
   */
  const downloadScoresReport = (documentType = "list") => {
    const payload = {
      school_cycle_id: filters.cycle?.id,
    };
    if (filters.group?.grade_group_grade_level_id) {
      payload.grade_group_grade_level_id =
        filters.group.grade_group_grade_level_id;
    }
    if (filters.programs?.id) {
      payload.program_id = filters.programs.id;
    }
    if (filters.levelProgram?.id) {
      payload.program_level_id = filters.levelProgram.id;
    }
    if (filters.subjects?.id) {
      payload.subject_id = filters.subjects.id;
    }
    if (filters.class?.id) {
      payload.class_id = filters.class.id;
    }
    if (periodSelected?.id) {
      payload.grading_period_id = periodSelected.id;
    }
    const isExcel = documentType === "excel";
    const method = isExcel ? GetScoresReportXlsx : GetScoresReportPdf;
    const mimeType = isExcel ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" : "application/pdf";
    const extension = isExcel ? "xlsx" : "pdf";
    setLoadingDownload(documentType);
    method(payload)
      .then((response) => {
        const url = window.URL.createObjectURL(
          new Blob([response.data], { type: mimeType }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${formatFilename(filters)}.${extension}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => {})
      .finally(() => setLoadingDownload(null));
  };

  /**
   * Function to format filters
   * @param {object} filters
   * @returns {string} Formatted filename
   */
  function formatFilename(filters) {
    const periodText = periodSelected
      ? t("class:table.period") + " " + periodSelected.label
      : t("class:filters.allThePeriods");
    return [
      filters?.cycle?.name,
      filters?.level?.name,
      filters?.subjects?.name,
      filters?.grade?.name,
      filters?.programs?.name,
      filters?.levelProgram?.name,
      filters?.group?.name,
      filters?.class?.title,
      periodText,
    ]
      .filter(Boolean)
      .join(" - ");
  }

  /**
   * Changes the value of the selected filters
   * @param {object} value
   * @param {string} filterType
   */
  const oldSelectFilters = (value, filterType) => {
    let newFilters = { ...filters };
    newFilters[filterType] = value;
    switch (filterType) {
      case "cycle":
        {
          const levels = value.school_levels;
          const subject = levels.length && levels[0].subjects;
          const formatLevels = levels.reduce((acc, level) => {
            if (level.type === "basic") {
              acc.push({
                label: level.name,
                value: level.organization_school_level_cycle_id,
              });
            }
            return acc;
          }, []);
          setSelectSchoolLevelsOptions(formatLevels);
          setSchoolLevels(levels);
          setSubjects(subject);
          setFilterSubjectsValue("");
          //Fill grade or level offers.
          if (levels[0] && levels[0].type == "superior") {
            setPrograms(
              levels[0].subjects.length && levels[0].subjects[0].programs,
            );
            setGrades([]);
          } else if (levels[0]) {
            setGrades(
              levels[0].subjects.length && levels[0].subjects[0].grades,
            );
            setPrograms([]);
          }
          newFilters.level = value?.school_levels?.[0];
          newFilters.subjects = value?.school_levels?.[0]?.subjects?.[0];
          newFilters.grade = null;
          newFilters.group = null;
          newFilters.programs = null;
          newFilters.levelProgram = null;

          //Format periods
          const periods = levels[0] && levels[0].grading_periods;
          for (const key in periods) {
            periods[key].label = Number.parseInt(key) + 1;
            periods[key].accessor = "period" + periods[key].id;
          }
          newFilters.class = null;
          setPeriods(periods);
          setPeriodSelected(false);
        }
        break;
      case "level":
        {
          setGrades([]);
          setSubjects(value?.subjects || []);
          setPrograms(value?.subjects?.[0]?.programs || []);
          setPeriods(value?.grading_periods || []);
          setFilterSubjectsValue("");
          //clean selector data
          newFilters.subjects = value?.subjects?.[0];
          newFilters.grade = null;
          newFilters.group = null;
          newFilters.programs = null;
          newFilters.levelProgram = null;

          //Format levels
          const periods = value.grading_periods;
          for (const key in periods) {
            periods[key].label = Number.parseInt(key) + 1;
            periods[key].accessor = "period" + periods[key].id;
          }
          newFilters.class = null;
          setPeriods(periods);
          setPeriodSelected(false);
        }
        break;
      case "subjects":
        setPrograms(value?.programs || []);
        setGrades(value?.grades || []);
        newFilters.grade = null;
        newFilters.group = null;
        newFilters.programs = null;
        newFilters.levelProgram = null;
        newFilters.class = null;
        setPeriodSelected(false);
        break;
      case "grade":
        setGroups(value?.groups || []);
        newFilters.group = null;
        newFilters.programs = null;
        newFilters.levelProgram = null;
        newFilters.class = null;
        setPeriodSelected(false);
        break;
      case "group":
        setClasses(value?.classes || []);
        newFilters.programs = null;
        newFilters.levelProgram = null;
        newFilters.class = null;
        setPeriodSelected(false);
        break;
      case "programs":
        setLevels(value?.program_levels || []);
        newFilters.levelProgram = null;
        newFilters.class = null;
        setPeriodSelected(false);
        break;
      case "levelProgram":
        setClasses(value?.classes || []);
        newFilters.class = null;
        setPeriodSelected(false);
        break;
      case "class":
        newFilters.class;
        break;
    }
    setFilters(newFilters);
    GetScoresFullInfo(newFilters);
  };

  /**
   * Changes the value of the selected filters
   * @param {object} value
   * @param {string} filterType
   */
  const selectFilters = async (value, filterType) => {
    let newFilters = { ...filters };
    newFilters[filterType] = value;
    switch (filterType) {
      case "cycle":
        {
          const levels = value.school_levels;
          const formatLevels = levels.reduce((acc, level) => {
            if (level.type === "basic") {
              acc.push({
                label: level.name,
                value: level.organization_school_level_cycle_id,
              });
            }
            return acc;
          }, []);
          setSelectSchoolLevelsOptions(formatLevels);
          setSchoolLevels(levels);
          const firstLevel = levels[0];
          const firstSubject = firstLevel?.subjects?.[0];
          setSubjects(firstLevel?.subjects || []);
          setFilterSubjectsValue("");
          newFilters.level = firstLevel;
          newFilters.subjects = null; // TODO: Show all subjects
          newFilters.class = null;
          // Fill grade/group or programs/levelProgram based on first level type
          if (firstLevel?.type === "superior") {
            const firstProgram = firstSubject?.programs?.[0];
            setPrograms(firstSubject?.programs || []);
            setLevels(firstProgram?.program_levels || []);
            setGrades([]);
            setGroups([]);
            // setClasses(firstProgram?.program_levels?.[0]?.classes || []);
            newFilters.grade = null;
            newFilters.group = null;
            newFilters.programs = firstProgram;
            newFilters.levelProgram = firstProgram?.program_levels?.[0];
            getAllClassesByLevel(newFilters);
          } else if (firstLevel) {
            const firstGrade = firstSubject?.grades?.[0];
            setGrades(firstSubject?.grades || []);
            setGroups(firstGrade?.groups || []);
            // setClasses(firstGrade?.groups?.[0]?.classes || []);
            setPrograms([]);
            setLevels([]);
            newFilters.grade = firstGrade;
            newFilters.group = firstGrade?.groups?.[0];
            newFilters.programs = null;
            newFilters.levelProgram = null;
            getAllClassesByLevel(newFilters);
          }
          // Format periods
          const periods = firstLevel?.grading_periods || [];
          await formatPeriods(periods);
          // for (const key in periods) {
          //   periods[key].label = Number.parseInt(key) + 1;
          //   periods[key].accessor = "period" + periods[key].id;
          // }
          // setPeriods(periods);
          setPeriodSelected(false);
        }
        break;
      case "level":
        {
          const subjects = value.subjects || [];
          const firstSubject = subjects[0];
          setSubjects(subjects);
          setFilterSubjectsValue("");
          setGrades(firstSubject.grades || []);
          setGroups(firstSubject.grades?.[0]?.groups || []);
          setPrograms(firstSubject.programs || []);
          // Clean selector data
          newFilters.subjects = null; // TODO: Show all subjects
          newFilters.class = null;
          // llena los valores
          if (value?.type === "superior") {
            // setClasses(
            //   firstSubject?.programs?.[0]?.program_levels?.[0]?.classes || [],
            // );
            newFilters.grade = null;
            newFilters.group = null;
            newFilters.programs = firstSubject?.programs?.[0];
            newFilters.levelProgram =
              firstSubject?.programs?.[0]?.program_levels?.[0];
            getAllClassesByLevel(newFilters);
          } else if (value?.type === "basic") {
            // setClasses(firstSubject?.grades?.[0]?.groups?.[0]?.classes || []);
            newFilters.grade = firstSubject?.grades?.[0];
            newFilters.group = firstSubject?.grades?.[0]?.groups?.[0];
            newFilters.programs = null;
            newFilters.levelProgram = null;
            getAllClassesByLevel(newFilters);
          }
          // Format periods
          const periods = value.grading_periods || [];
          await formatPeriods(periods);
          // for (const key in periods) {
          //   periods[key].label = Number.parseInt(key) + 1;
          //   periods[key].accessor = "period" + periods[key].id;
          // }
          // setPeriods(periods);
          setPeriodSelected(false);
        }
        break;
      case "subjects":
        getAllClassesByLevel(newFilters);
        newFilters.class = null;
        break;
      case "grade":
        {
          const groups = value?.groups || [];
          const firstGroup = value ? groups[0] : null;
          setGroups(groups);
          // setClasses(firstGroup?.classes || []);
          newFilters.group = firstGroup || null;
          newFilters.subjects = null; // TODO: Show all subjects
          newFilters.class = null;
          newFilters.programs = null;
          newFilters.levelProgram = null;
          newFilters.class = null;
          setPeriodSelected(false);
          getAllClassesByLevel(newFilters);
        }
        break;
      case "group":
        {
          // setClasses(value?.classes || []);
          newFilters.programs = null;
          newFilters.levelProgram = null;
          newFilters.subjects = null; // TODO: Show all subjects
          newFilters.class = null;
          setPeriodSelected(false);
          getAllClassesByLevel(newFilters);
        }
        break;
      case "programs":
        setLevels(value?.program_levels || []);
        newFilters.levelProgram = value?.program_levels?.[0] || null;
        newFilters.subjects = null; // TODO: Show all subjects
        newFilters.class = null;
        setPeriodSelected(false);
        break;
      case "levelProgram":
        {
          // setClasses(value?.classes || []);
          newFilters.subjects = null; // TODO: Show all subjects
          newFilters.class = null;
          setPeriodSelected(false);
          getAllClassesByLevel(newFilters);
        }
        break;
      case "class":
        newFilters.class;
        break;
    }
    setFilters(newFilters);
    GetScoresFullInfo(newFilters);
  };

  //Export to Excel
  const downloadFile = (exportOptions, exportData) => {
    ExcellentExport.convert(exportOptions, exportData);
  };

  /*
   * Initial Loading
   */
  useEffect(() => {
    getCyclesFullInformation();
  }, []);
  /**
   * Description: Function to set headers according to filters
   * @returns header elements
   */
  const getDataFilters = () => {
    return (
      <Container className="fs-6" fluid>
        <Row className="mt-3">
          <Col>
            <b>{t("class:headerScoresPDF.schoolCycle")}</b>
            {filters.cycle?.name}
          </Col>
          <Col>
            <b>{t("class:headerScoresPDF.subject")}</b>
            {filters.subjects?.name}
          </Col>
        </Row>
        <Row className="mt-2">
          <Col>
            <b>{t("class:headerScoresPDF.schooling")}</b>
            {filters.level?.name}
          </Col>
          <Col>
            <b>{t("class:headerScoresPDF.period")}</b>
            {periodSelected?.label || t("class:filters.textAll")}
          </Col>
        </Row>
        {filters.level?.type === "superior" ? (
          <>
            <Row className="mt-2">
              <Col>
                <b>{t("class:headerScoresPDF.academicOffer")}</b>
                {filters.programs?.name}
              </Col>
            </Row>
            <Row className="mt-2">
              <Col>
                <b>{t("class:headerScoresPDF.levelOffer")}</b>
                {filters.levelProgram?.name || t("class:filters.textAll")}
              </Col>
            </Row>
          </>
        ) : (
          <>
            <Row className="mt-2">
              <Col>
                <b>{t("class:headerScoresPDF.grade")}</b>
                {filters.grade?.name || t("class:filters.textAll")}
              </Col>
            </Row>
            <Row className="mt-2">
              <Col>
                <b>{t("class:headerScoresPDF.group")}</b>
                {filters.group?.name || t("class:filters.textAll")}
              </Col>
            </Row>
          </>
        )}
      </Container>
    );
  };

  const oldScoreFilters = () => {
    return (
      <Col md={8}>
        <Dropdown className="ms-2 d-inline-block">
          <Dropdown.Toggle
            id="schoolLevel"
            variant="outline-secondary"
            disabled={!filters?.cycle?.name}
          >
            {filters?.level?.name
              ? filters?.level?.name
              : t("class:filters.selectLevel")}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {schoolLevels &&
              schoolLevels.map((level) => {
                return (
                  <Dropdown.Item
                    key={level.value}
                    onClick={() => {
                      oldSelectFilters(level, "level");
                    }}
                  >
                    {level.name}
                  </Dropdown.Item>
                );
              })}
          </Dropdown.Menu>
        </Dropdown>
        <Dropdown
          className="ms-2 d-inline-block text-break"
          style={{ maxWidth: "200px" }}
        >
          <Dropdown.Toggle
            id="subject"
            variant="outline-secondary"
            disabled={!filters?.level?.name}
            style={{ maxWidth: "200px" }}
            className="text-truncate"
          >
            {filters?.subjects?.name
              ? filters?.subjects?.name
              : t("class:table.subject")}
          </Dropdown.Toggle>
          <Dropdown.Menu
            className="text-truncate"
            style={{ maxWidth: "200px" }}
          >
            <input
              className="mx-1"
              style={{ width: "95%" }}
              autoFocus
              onChange={(e) => setFilterSubjectsValue(e.target.value)}
              value={filterSubjectsValue}
            />

            {subjects &&
              subjects
                .filter(
                  (subject) =>
                    !filterSubjectsValue ||
                    subject.name
                      .toLowerCase()
                      .includes(filterSubjectsValue.toLowerCase()),
                )
                .map((subject) => {
                  return (
                    <Dropdown.Item
                      key={subject.value}
                      onClick={() => {
                        oldSelectFilters(subject, "subjects");
                        setFilterSubjectsValue("");
                      }}
                      className="text-truncate"
                    >
                      {subject.name}
                    </Dropdown.Item>
                  );
                })}
          </Dropdown.Menu>
        </Dropdown>
        {filters?.level && filters?.level?.type === "superior" ? (
          <>
            {/* Academic Offer */}
            <Dropdown className="ms-2" style={{ display: "inline-block" }}>
              <Dropdown.Toggle
                id="academicOffer"
                variant="outline-secondary"
                disabled={!filters?.subjects?.name}
              >
                {filters?.programs
                  ? filters?.programs?.name
                  : t("class:filters.selectAcademicOffer")}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {programs &&
                  programs.map((programs) => {
                    return (
                      <Dropdown.Item
                        key={programs.value}
                        onClick={() => {
                          oldSelectFilters(programs, "programs");
                        }}
                      >
                        {programs.name}
                      </Dropdown.Item>
                    );
                  })}
              </Dropdown.Menu>
            </Dropdown>

            <Dropdown className="ms-2" style={{ display: "inline-block" }}>
              <Dropdown.Toggle
                id="levelProgram"
                variant="outline-secondary"
                disabled={!filters?.programs?.name}
              >
                {filters?.levelProgram?.name || t("class:modalImport.level")}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item
                  key={"allProgramLevels"}
                  onClick={() => {
                    oldSelectFilters(null, "levelProgram");
                  }}
                >
                  {t("class:filters.textAll")}
                </Dropdown.Item>
                {levels &&
                  levels.map((levels) => {
                    return (
                      <Dropdown.Item
                        key={levels.value}
                        onClick={() => {
                          oldSelectFilters(levels, "levelProgram");
                        }}
                      >
                        {levels.name}
                      </Dropdown.Item>
                    );
                  })}
              </Dropdown.Menu>
            </Dropdown>
          </>
        ) : (
          <>
            <Dropdown className="ms-2" style={{ display: "inline-block" }}>
              <Dropdown.Toggle
                id="grade"
                variant="outline-secondary"
                disabled={!filters?.subjects?.name}
              >
                {filters?.grade?.name || t("class:table.grade")}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item
                  key="allGrades"
                  onClick={() => {
                    oldSelectFilters(null, "grade");
                  }}
                >
                  {t("class:filters.textAll")}
                </Dropdown.Item>
                {grades &&
                  grades.map((grade) => {
                    return (
                      <Dropdown.Item
                        key={grade.name}
                        onClick={() => {
                          oldSelectFilters(grade, "grade");
                        }}
                      >
                        {grade.name}
                      </Dropdown.Item>
                    );
                  })}
              </Dropdown.Menu>
            </Dropdown>
            <Dropdown className="ms-2" style={{ display: "inline-block" }}>
              <Dropdown.Toggle
                id="group"
                variant="outline-secondary"
                disabled={!filters?.grade?.name}
              >
                {filters?.group?.name || t("class:table.group")}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item
                  key="allGroups"
                  onClick={() => {
                    oldSelectFilters(null, "group");
                  }}
                >
                  {t("class:filters.textAll")}
                </Dropdown.Item>
                {groups &&
                  groups.map((group) => {
                    return (
                      <Dropdown.Item
                        key={group.value}
                        onClick={() => {
                          oldSelectFilters(group, "group");
                        }}
                      >
                        {group.name}
                      </Dropdown.Item>
                    );
                  })}
              </Dropdown.Menu>
            </Dropdown>
          </>
        )}
        <Dropdown className="ms-2" style={{ display: "inline-block" }}>
          <Dropdown.Toggle
            id="periods"
            variant="outline-secondary"
            disabled={!filters?.level?.name && !filters?.levelProgram?.name}
          >
            {periodSelected != false
              ? t("class:table.period") + " " + periodSelected?.label
              : t("class:filters.allThePeriods")}
          </Dropdown.Toggle>
          <Dropdown.Menu style={{ maxHeight: "200px", overflowY: "auto" }}>
            <Dropdown.Item
              key="allPeriods"
              onClick={() => {
                setPeriodSelected(false);
                setLoading(true);
                setTimeout(() => {
                  setLoading(false);
                }, 300);
              }}
            >
              {t("class:filters.allThePeriods")}
            </Dropdown.Item>
            {periods &&
              periods.map((period) => {
                return (
                  <Dropdown.Item
                    key={period.value}
                    onClick={() => {
                      setPeriodSelected(period);
                      setLoading(true);
                      setTimeout(() => {
                        setLoading(false);
                      }, 300);
                    }}
                  >
                    {t("class:table.period") + " " + period.label}
                  </Dropdown.Item>
                );
              })}
          </Dropdown.Menu>
        </Dropdown>
        {(filters?.levelProgram?.name || filters?.group?.name) && (
          <Dropdown className="ms-2" style={{ display: "inline-block" }}>
            <Dropdown.Toggle
              id="classes"
              variant="outline-secondary"
              disabled={!filters?.group?.name && !filters?.levelProgram?.name}
            >
              {filters?.class?.title || t("class:main.class")}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                key="allClasses"
                onClick={() => {
                  oldSelectFilters(null, "classes");
                }}
              >
                {t("class:filters.allClasses")}
              </Dropdown.Item>
              {classes &&
                classes.map((classes) => {
                  return (
                    <Dropdown.Item
                      key={classes.value}
                      onClick={() => {
                        {
                          oldSelectFilters(classes, "class");
                        }
                      }}
                    >
                      {classes.title}
                    </Dropdown.Item>
                  );
                })}
            </Dropdown.Menu>
          </Dropdown>
        )}
        <p
          className="ms-3 d-inline"
          style={{ color: "#c8cbcc", fontWeight: "bold", fontSize: "small" }}
        >
          {t("global:totalRecords", { count: count })}
        </p>
      </Col>
    );
  };

  const scoreFilters = () => {
    return (
      <Col md={8}>
        {/* School Level */}
        <Dropdown className="ms-2 d-inline-block">
          <Dropdown.Toggle
            id="schoolLevel"
            variant="outline-secondary"
            disabled={!filters?.cycle?.name}
          >
            {filters?.level?.name
              ? filters?.level?.name
              : t("class:filters.selectLevel")}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {schoolLevels &&
              schoolLevels.map((level) => {
                return (
                  <Dropdown.Item
                    key={level.value}
                    onClick={() => {
                      selectFilters(level, "level");
                    }}
                  >
                    {level.name}
                  </Dropdown.Item>
                );
              })}
          </Dropdown.Menu>
        </Dropdown>
        {/* Academic Offer */}
        {filters?.level && filters?.level?.type === "superior" ? (
          <>
            {/* Academic Offer */}
            <Dropdown className="ms-2" style={{ display: "inline-block" }}>
              <Dropdown.Toggle
                id="academicOffer"
                variant="outline-secondary"
                disabled={!filters?.subjects?.name && !isFFReportScores}
              >
                {filters?.programs
                  ? filters?.programs?.name
                  : t("class:filters.selectAcademicOffer")}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {programs &&
                  programs.map((programs) => {
                    return (
                      <Dropdown.Item
                        key={programs.value}
                        onClick={() => {
                          selectFilters(programs, "programs");
                        }}
                      >
                        {programs.name}
                      </Dropdown.Item>
                    );
                  })}
              </Dropdown.Menu>
            </Dropdown>
            <Dropdown className="ms-2" style={{ display: "inline-block" }}>
              <Dropdown.Toggle
                id="levelProgram"
                variant="outline-secondary"
                disabled={!filters?.programs?.name}
              >
                {filters?.levelProgram?.name || t("class:modalImport.level")}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {levels &&
                  levels.map((levels) => {
                    return (
                      <Dropdown.Item
                        key={levels.value}
                        onClick={() => {
                          selectFilters(levels, "levelProgram");
                        }}
                      >
                        {levels.name}
                      </Dropdown.Item>
                    );
                  })}
              </Dropdown.Menu>
            </Dropdown>
          </>
        ) : (
          <>
            <Dropdown className="ms-2" style={{ display: "inline-block" }}>
              <Dropdown.Toggle
                id="grade"
                variant="outline-secondary"
                disabled={!filters?.subjects?.name && !isFFReportScores}
              >
                {filters?.grade?.name || t("class:table.grade")}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {grades &&
                  grades.map((grade) => {
                    return (
                      <Dropdown.Item
                        key={grade.name}
                        onClick={() => {
                          selectFilters(grade, "grade");
                        }}
                      >
                        {grade.name}
                      </Dropdown.Item>
                    );
                  })}
              </Dropdown.Menu>
            </Dropdown>
            <Dropdown className="ms-2" style={{ display: "inline-block" }}>
              <Dropdown.Toggle
                id="group"
                variant="outline-secondary"
                disabled={!filters?.grade?.name}
              >
                {filters?.group?.name || t("class:table.group")}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {groups &&
                  groups.map((group) => {
                    return (
                      <Dropdown.Item
                        key={group.value}
                        onClick={() => {
                          selectFilters(group, "group");
                        }}
                      >
                        {group.name}
                      </Dropdown.Item>
                    );
                  })}
              </Dropdown.Menu>
            </Dropdown>
          </>
        )}
        {/* Periods */}
        <Dropdown className="ms-2" style={{ display: "inline-block" }}>
          <Dropdown.Toggle
            id="periods"
            variant="outline-secondary"
            disabled={!filters?.level?.name && !filters?.levelProgram?.name}
          >
            {periodSelected != false
              ? t("class:table.period") + " " + periodSelected?.label
              : t("class:filters.allThePeriods")}
          </Dropdown.Toggle>
          <Dropdown.Menu style={{ maxHeight: "200px", overflowY: "auto" }}>
            <Dropdown.Item
              key="allPeriods"
              onClick={() => {
                setPeriodSelected(false);
                setLoading(true);
                setTimeout(() => {
                  setLoading(false);
                }, 300);
              }}
            >
              {t("class:filters.allThePeriods")}
            </Dropdown.Item>
            {periods &&
              periods.map((period) => {
                return (
                  <Dropdown.Item
                    key={period.value}
                    onClick={() => {
                      setPeriodSelected(period);
                      setLoading(true);
                      setTimeout(() => {
                        setLoading(false);
                      }, 300);
                    }}
                  >
                    {t("class:table.period") + " " + period.label}
                  </Dropdown.Item>
                );
              })}
          </Dropdown.Menu>
        </Dropdown>
        {/* Subject */}
        <Dropdown
          className="ms-2 d-inline-block text-break"
          style={{ maxWidth: "200px" }}
        >
          <Dropdown.Toggle
            id="subject"
            variant="outline-secondary"
            disabled={!filters?.level?.name}
            style={{ maxWidth: "200px" }}
            className="text-truncate"
          >
            {filters?.subjects?.name
              ? filters?.subjects?.name
              : t("class:filters.allSubjects")}
          </Dropdown.Toggle>
          <Dropdown.Menu
            className="text-truncate"
            style={{ maxWidth: "200px" }}
          >
            <input
              className="mx-1"
              style={{ width: "95%" }}
              autoFocus
              onChange={(e) => setFilterSubjectsValue(e.target.value)}
              value={filterSubjectsValue}
            />
            <Dropdown.Item
              key="allSubjects"
              onClick={() => {
                selectFilters(null, "subjects");
                setFilterSubjectsValue("");
              }}
            >
              {t("class:filters.allSubjects")}
            </Dropdown.Item>
            {subjects &&
              subjects
                .filter(
                  (subject) =>
                    !filterSubjectsValue ||
                    subject.name
                      .toLowerCase()
                      .includes(filterSubjectsValue.toLowerCase()),
                )
                .map((subject) => {
                  return (
                    <Dropdown.Item
                      key={subject.value}
                      onClick={() => {
                        selectFilters(subject, "subjects");
                        setFilterSubjectsValue("");
                      }}
                      className="text-truncate"
                    >
                      {subject.name}
                    </Dropdown.Item>
                  );
                })}
          </Dropdown.Menu>
        </Dropdown>
        {/* Classes */}
        {(filters?.levelProgram?.name || filters?.group?.name) && (
          <Dropdown className="ms-2" style={{ display: "inline-block" }}>
            <Dropdown.Toggle
              id="classes"
              variant="outline-secondary"
              disabled={!filters?.group?.name && !filters?.levelProgram?.name}
            >
              {filters?.class?.title || t("class:filters.allClasses")}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                key="allClasses"
                onClick={() => {
                  selectFilters(null, "class");
                }}
              >
                {t("class:filters.allClasses")}
              </Dropdown.Item>
              {classes &&
                classes.map((classes) => {
                  return (
                    <Dropdown.Item
                      key={classes.value}
                      onClick={() => {
                        {
                          selectFilters(classes, "class");
                        }
                      }}
                    >
                      {classes.title}
                    </Dropdown.Item>
                  );
                })}
            </Dropdown.Menu>
          </Dropdown>
        )}
        {/* Total records */}
        <p
          className="ms-3 d-inline"
          style={{ color: "#c8cbcc", fontWeight: "bold", fontSize: "small" }}
        >
          {t("global:totalRecords", { count: count })}
        </p>
      </Col>
    );
  };

  return (
    <div>
      <Row className="mb-3">
        <Col md={12} className="mb-4 ms-2">
          <Dropdown style={{ display: "inline-block" }}>
            <Dropdown.Toggle
              id="downloadOptionCycle"
              variant="outline-secondary"
              disabled={!cyclesForScores.length}
            >
              {filters?.cycle?.name
                ? filters?.cycle.name
                : t("class:filters:selectCycle")}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {cyclesForScores && cyclesForScores.length > 0 ? (
                cyclesForScores.map((cycle) => (
                  <Dropdown.Item
                    key={cycle.value}
                    onClick={() => {
                      isFFReportScores
                        ? selectFilters(cycle, "cycle")
                        : oldSelectFilters(cycle, "cycle");
                    }}
                  >
                    {cycle.name}
                  </Dropdown.Item>
                ))
              ) : (
                <Dropdown.Item disabled>
                  {t("global:select.noMatches")}
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
        </Col>
        <FeatureFlagWrapper
          name="Reporte_Calificaciones_Detallado"
          fallback={oldScoreFilters()}
        >
          {scoreFilters()}
        </FeatureFlagWrapper>
        <Col md={4}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              float: "right",
            }}
          >
            {/* Modal for grading criteria */}
            {permissions?.granding_criteria?.access &&
              permissions?.granding_criteria?.create &&
              permissions?.granding_criteria?.edit &&
              permissions?.granding_criteria?.delete &&
              validateUserRoleIds([1, 2]) && (
                <ModalGradingCriteria
                  openButtonStyle={{ marginRight: "10px" }}
                  schoolLevels={selectSchoolLevelsOptions}
                  isLoading={loading}
                  cyclesForScores={cyclesForScores}
                />
              )}
            {validateUserRoleIds([1, 2]) && (
              <Button
                variant="outline-secondary"
                className="break-word me-2"
                onClick={() => setShowModalReleaseRatings(true)}
                disabled={loading}
              >
                {t("class:main.releaseRatings")}
              </Button>
            )}
            <Dropdown className="float-end" style={{ display: "inline-block" }}>
              <Dropdown.Toggle
                id="dropdownReportCards"
                variant="outline-secondary"
                disabled={!studentForTable.length}
              >
                <i className="bi bi-download"></i>
                {t("global:buttons.download")}
              </Dropdown.Toggle>
              <Dropdown.Menu disabled={printExport}>
                {actionOptions.map((action) => {
                  return (
                    <FeatureFlagWrapper
                      key="downloadDocuments"
                      name="Reporte_Calificaciones_Detallado"
                      fallback={
                        action.value == "list" ? (
                          <ReactToPrint
                            trigger={() => (
                              <Dropdown.Item>{t(action.label)}</Dropdown.Item>
                            )}
                            content={() => componentRef.current}
                            documentTitle={formatFilename(filters)}
                          />
                        ) : (
                          <ReactToPrint
                            trigger={() => (
                              <Dropdown.Item>{t(action.label)}</Dropdown.Item>
                            )}
                            content={() =>
                              downloadFile(exportOptions, exportData)
                            }
                          />
                        )
                      }
                    >
                      <Dropdown.Item
                        onClick={() => downloadScoresReport(action.value)}
                        disabled={loadingDownload === action.value}
                      >
                        {t(action.label)}
                        {loadingDownload === action.value && (
                          <span
                            className="spinner-border spinner-border-sm ms-2"
                            role="status"
                          />
                        )}
                      </Dropdown.Item>
                    </FeatureFlagWrapper>
                  );
                })}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </Col>
      </Row>
      {/* TABLE */}
      {loading ? (
        <ContentSolid className="mt-3">
          <LoadingTable />
        </ContentSolid>
      ) : (
        <ContentTable startColumFilter={0} lastColumFilter={4}>
          <Table
            ref={componentRef}
            columns={columns}
            data={data}
            setExportData={setExportData}
            setPrintExport={setPrintExport}
            limitRowsShown={50}
            setRowsDisplayed={setCount}
            headerInformation={getDataFilters}
          ></Table>
        </ContentTable>
      )}

      {showModalReleaseRatings && (
        <ModalReleaseRatings
          setShowModalReleaseRatings={setShowModalReleaseRatings}
          schoolLevels={selectSchoolLevelsOptions}
        />
      )}
    </div>
  );
};

ScoresView.propTypes = {
  value: PropTypes.object.isRequired,
  cell: PropTypes.object,
};
export default ScoresView;
